import React, { useRef, useState } from 'react';
import { Button, Grid, TextField, MenuItem, Box, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { lightTheme, darkTheme } from '../../../../../../platform/assets/css/theme';
import { useReactToPrint } from 'react-to-print';
import { MonthYearSelector } from '../month/monthYearSelector';
import PrintableView from './printableView';
import GeoCalendar from '../geoCalendar';
import { CalendarViewsProvider } from '../view/calendarViewsProvider';
import { colorNameToHex } from '../../../../../../platform/util/colorMap';
import { useCalendar } from '../calendarContext';
import { getEthiopianDate } from '../../../../../../platform/util/ethiopia/ethiopianCalendar';
const HolidayColors = {
  NATIONAL: colorNameToHex.DeepSkyBlue,
  RELIGIOUS: colorNameToHex.Purple,
  ANNUAL_CELEBRATION: colorNameToHex.Coral,
};

const HolidayLabels = {
  NATIONAL: { en: 'National', am: 'ብሔራዊ' },
  RELIGIOUS: { en: 'Religious', am: 'ሃይማኖታዊ' },
  ANNUAL_CELEBRATION: { en: 'Annual Celebration', am: 'ዓመታዊ' },
}; 

const bibleQuotesByMonth = {
  1: {
    verse: 'Isaiah 43:19',
    quote: 'Behold, I will do a new thing; now it shall spring forth; shall you not know it?',
    amharicQuote: 'እነሆ፤ አዲስ ነገር አደርጋለሁ! እርሱም አሁን ይበቅላል፤ አታስተውሉትምን? በምድረ በዳም መንገድ አዘጋጃለሁ፤ በበረሓም ምንጭ አፈልቃለሁ፤'
  },
  2: {
    verse: 'Philippians 4:13',
    quote: 'I can do all things through Christ who strengthens me.',
    amharicQuote: 'ኀይልን በሚሰጠኝ በርሱ ሁሉን ማድረግ እችላለሁ።'
  },
  3: {
    verse: 'Joshua 1:9',
    quote: 'Be strong and courageous. Do not be afraid... for the Lord your God is with you wherever you go.',
    amharicQuote: 'በምትሄድበት ሁሉ እግዚአብሔር አምላክህ ከአንተ ጋራ ነውና አይዞህ፤ በርታ፤ ጽና፤ አትፍራ፤ አትደንግጥ፤'
  },
  4: {
    verse: 'Romans 12:12',
    quote: 'Peace I leave with you. My peace I give to you; not as the world gives, give I to you. Don\'t let your heart be troubled, neither let it be fearful.',
    amharicQuote: 'ሰላምን እተውላችኋለሁ፥ ሰላሜን እሰጣችኋለሁ፤ እኔ የምሰጣችሁ ዓለም እንደሚሰጥ አይደለም። ልባችሁ አይታወክ አይፍራም።'
  },
  5: {
    verse: 'John 14:27',
    quote: 'Peace I leave with you; my peace I give to you. Do not let your hearts be troubled.',
    amharicQuote: 'ሰላምን እተውላችኋለሁ፤ ሰላሜን እሰጣችኋለሁ፤ ልባችሁ አይታወክ፤ አይፍራም።'
  },
  6: {
    verse: 'Proverbs 3:5‑6',
    quote: 'Trust in the LORD with all your heart and lean not on your own understanding; in all your ways submit to him, and he will make your paths straight',
    amharicQuote: 'በፍጹም ልብህ በእግዚአብሔር ታመን፥ በራስህም ማስተዋል አትደገፍ፤በመንገድህ ሁሉ እርሱን እወቅ፥ እርሱም ጎዳናህን ያቀናልሃል።'
  },
  7: {
    verse: '2 Corinthians 5:17',
    quote: 'Therefore if anyone is in Christ, he is a new creation. The old things have passed away. Behold, all things have become new.',
    amharicQuote: 'ስለዚህ ማንም በክርስቶስ ቢሆን አዲስ ፍጥረት ነው፤ አሮጌው ነገር አልፎአል፤ እነሆ፥ ሁሉም አዲስ ሆኖአል።'
  },
  8: {
    verse: 'Psalm 91:1',
    quote: 'He who dwells in the shelter of the Most High will rest in the shadow of the Almighty.',
    amharicQuote: 'በልዑል መጠጊያ የሚኖር ሁሉን በሚችል አምላክ ጥላ ውስጥ ያድራል።'
  },
  9: {
    verse: '1 Thessalonians 5:18',
    quote: 'Give thanks in all circumstances; for this is God’s will for you in Christ Jesus.',
    amharicQuote: 'በነገር ሁሉ አመስግኑ፤ ይህ በክርስቶስ ኢየሱስ ለእናንተ የእግዚአብሔር ፈቃድ ነው።'
  },
  10: {
    verse: 'James 1:5',
    quote: 'If any of you lacks wisdom, let him ask God, who gives generously to all without reproach, and it will be given him.',
    amharicQuote: 'ከእናንተ ግን ማንም ጥበብ ቢጎድለው፥ ሳይነቅፍ በልግስና ለሁሉ የሚሰጠውን እግዚአብሔርን ይለምን፥ ለእርሱም ይሰጠዋል።'
  },
  11: {
    verse: 'Jeremiah 29:11',
    quote: 'For I know the thoughts that I think toward you, says Yahweh, thoughts of peace, and not of evil, to give you hope in your latter end.',
    amharicQuote: 'ለእናንተ የማስባትን አሳብ እኔ አውቃለሁ፤ ፍጻሜና ተስፋ እሰጣችሁ ዘንድ የሰላም አሳብ ነው እንጂ የክፉ ነገር አይደለም። '
  },
  12: {
    verse: '2 Timothy 1:7',
    quote: 'For God has not given us a spirit of fear, but of power and of love and of a sound mind.',
    amharicQuote: 'እግዚአብሔር የኃይልና የፍቅር ራስንም የመግዛት መንፈስ እንጂ የፍርሃት መንፈስ አልሰጠንም።'
  }
};



const CountryCodeLegend = () => {
  const countryCodes = [
    { code: 'ET', en: 'Ethiopian Holiday', am: 'የኢትዮጵያ በዓል' },
    { code: 'US', en: 'United States Holiday', am: 'የአሜሪካ በዓል' },
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '0.25rem',
        fontSize: '0.45rem',
        justifyContent: 'flex-start',
        marginTop: '0.1rem',
        pageBreakInside: 'avoid',
      }}
    >
      {countryCodes.map(({ code, en, am }) => (
        <div key={code} style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <span style={{ fontWeight: 'bold' }}>[{code}]</span>
            <span>{en}</span>
          </div>
          <div style={{ paddingLeft: '1.6rem', fontSize: '0.45rem', color: '#555' }}>{am}</div>
        </div>
      ))}
    </div>
  );
};



const HolidayLegend = () => {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.25rem',
        fontSize: '0.5rem',
        justifyContent: 'flex-start',
        marginTop: '0.1rem',
        pageBreakInside: 'avoid',
      }}
    >
      {Object.entries(HolidayColors).map(([key, color]) => {
        const label = HolidayLabels[key];
        return (
          <div
            key={key}
            style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                backgroundColor: color,
                borderRadius: 2,
                border: '1px solid #aaa',
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span>{label.en}</span>
              <span style={{ fontSize: '0.5rem', color: '#555' }}>{label.am}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};


const LegendFooter = () => {
  const { currentMonth } = useCalendar();
  const { ethiopianMonth } = getEthiopianDate(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    currentMonth.getDate()
  );
  const quote = bibleQuotesByMonth[ethiopianMonth];
  return (
    <div
      className="print-calendar-footer"
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(110px, 0.7fr) minmax(0, 2fr) minmax(110px, 0.7fr)',
        gap: '0.35rem',
        alignItems: 'flex-start',
        pageBreakInside: 'avoid',
      }}
    >
      <HolidayLegend /> 
      {quote && (
        <div style={{ fontStyle: 'italic', lineHeight: 1.35, minWidth: 0 }}>
          {/* English Line */}
          <div style={{ fontSize: '0.55rem' }}>
            <em>{quote.verse}</em>: “{quote.quote}”
          </div>

          {/* Amharic Line */}
          <div style={{ fontSize: '0.5rem', color: '#444', marginTop: '0.25rem' }}>
            {quote.amharicQuote}
          </div>
        </div>
      )}

      <CountryCodeLegend />
    </div>
  );
};
const PrintView = () => {
  const componentRef = useRef();
  const [settings, setSettings] = useState({
    height: '8.27in',
    width: '11.69in',
    theme: 'light',
  });

  const [isPreview, setIsPreview] = useState(0);

  const handleSettingChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const togglePreview = () => {
    setIsPreview((prev) => !prev);
  };

  const handleTriggerPrint = () => {
    handlePrint();
  };


  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `
    @page {
      size: A4 landscape;
      margin: 0;
    }
  
    @media print {
      html,
      body {
        margin: 0;
        padding: 0;
        width: auto !important;
        height: auto !important;
        overflow: visible !important;
        background: #fff !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
  
      .printable-calendar {
        width: calc(${settings.width} - 0.14in);
        height: auto;
        margin-left: 0.14in;
        /* Extra left gutter for binding with a minimal outer page inset. */
        padding: 0.05in 0.02in 0.05in 0.42in;
        box-sizing: border-box;
        overflow: visible;
        break-inside: avoid;
        page-break-inside: avoid;
        break-after: avoid;
        page-break-after: avoid;
        background: ${settings.theme === 'dark' ? '#333' : '#fff'};
        color: ${settings.theme === 'dark' ? '#fff' : '#222'}; 
         ${false
        ? `
Featured Businesses























             zoom: 86%;
          `
        : ''
        }
      }

      .print-calendar-footer {
        margin-top: 0.025in;
        break-before: avoid;
        page-break-before: avoid;
      }

      .calendar-month-heading {
        padding: 0 0.1in 0.035in !important;
        transform: translateY(-0.06in);
        margin-bottom: 0 !important;
      }

      .calendar-header {
        font-size: 2.1rem !important;
        line-height: 1.05 !important;
      }

      .calendar-month-paper {
        box-shadow: none !important;
        border-radius: 0 !important;
      }
  
      .calendar-cell {
        background: #fff !important;
        box-sizing: border-box !important;
        border: 0 !important;
        border-right: 0.5px solid #71809d !important;
        border-bottom: 0.5px solid #71809d !important;
      }

      .calendar-cell--outside-month {
        background: #eef1f4 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }

      .calendar-cell--today {
        background: #fff !important;
      }
  
      .calendar-cell.row-0 {
        border-top: 0.5px solid #71809d !important;
      }

      .calendar-cell.col-0 {
        border-left: 0.75px solid #71809d !important;
      }
    }
  `
  });



  const viewOnRemoteTV = () => {
    const newWindow = window.open('', '_blank', 'width=800,height=600');
    if (newWindow) {
      newWindow.document.body.innerHTML = `
        <div style="background-color: ${settings.theme === 'dark' ? '#333' : '#fff'}; color: ${settings.theme === 'dark' ? '#fff' : '#000'};">
          <h3>Printable View</h3>
          <div>${componentRef.current ? componentRef.current.innerHTML : ''}</div>
        </div>
      `;
      newWindow.document.title = 'Printable View';
    }
  };

  return (
    <Grid
      container
      sx={{
        width: '100%',
        minHeight: '100vh', // ✅ allow content to grow
        overflowX: 'hidden', // Prevent horizontal scroll
        margin: 0,
        padding: 0,
        backgroundColor: 'background.default',
        color: 'text.primary',
      }}
    >
      {!isPreview && (
        <Grid
          item
          xs={12}
          sm={4}
          md={3}
          lg={2}
          sx={{
            backgroundColor: 'background.paper',
            height: '100%',
            padding: 2,
            borderRight: '1px solid',
            borderColor: 'divider',
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography component="h1" variant="h6" sx={{ fontWeight: 600 }}>Print settings</Typography>
            <Typography variant="body2" color="text.secondary">
              Optimized for A4 landscape
            </Typography>

            <TextField
              label="Height"
              name="height"
              value={settings.height}
              onChange={handleSettingChange}
              fullWidth
              helperText="A4 landscape: 8.27in"
            />
            <TextField
              label="Width"
              name="width"
              value={settings.width}
              onChange={handleSettingChange}
              fullWidth
              helperText="A4 landscape: 11.69in"
            />
            <TextField
              select
              label="Print theme"
              name="theme"
              value={settings.theme}
              onChange={handleSettingChange}
              fullWidth
            >
              <MenuItem value="light">Light</MenuItem>
              <MenuItem value="dark">Dark</MenuItem>
            </TextField>

            <Button variant="contained" onClick={handleTriggerPrint}>
              Print
            </Button>
          </Box>

          <Box sx={{ marginTop: 4 }}>
            <GeoCalendar />
          </Box>
        </Grid>
      )}

      <Grid
        item
        xs={12}
        sm={isPreview ? 12 : 8}
        md={isPreview ? 12 : 9}
        lg={isPreview ? 12 : 10}
        container
        direction="column"
        sx={{
          width: '100%',
          overflowX: 'hidden', // Prevent scroll
          backgroundColor: 'background.default',
        }}
      >
        {!isPreview && (
          <Grid item sx={{ padding: '10px' }}>
            <MonthYearSelector />
          </Grid>
        )}
        <Grid
          item
          sx={{
            padding: '16px',
            width: '100%',
            backgroundColor: 'background.default',
            overflow: 'visible', // ✅ ensures no internal scroll
          }}
        >
          {/* Button Row at Top */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              mb: 2,
            }}
          >
            <Button
              variant="contained"
              size="small"
              onClick={viewOnRemoteTV}
            >
              View on TV
            </Button>

            <Button
              variant="contained"
              size="small"
              onClick={togglePreview}
            >
              {isPreview ? 'Exit Preview' : 'Preview'}
            </Button>
          </Box>

          {/* Printable Content Area */}

          <ThemeProvider theme={settings.theme === 'dark' ? darkTheme : lightTheme}>
          <div
            ref={componentRef}
            className="printable-calendar"
            style={{ backgroundColor: settings.theme === 'dark' ? '#333' : '#fff', color: settings.theme === 'dark' ? '#fff' : '#222' }}
          >
            <CalendarViewsProvider>
              <PrintableView printPreviewHeight={settings.height} />
              <LegendFooter />             
            </CalendarViewsProvider>
          </div>
          </ThemeProvider>
        </Grid>

      </Grid>
    </Grid>
  );
};



export default PrintView;
